<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import AiBotAPI from 'dashboard/api/aiBot';
import {
  aiUsageEventLabelKey,
  normalizeAiCreditSnapshot,
} from 'dashboard/helper/aiBotUsage';
import { platformIntlLocale } from 'dashboard/helper/platformLocale';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ReportFilters from './components/ReportFilters.vue';
import ReportHeader from './components/ReportHeader.vue';

const MAX_RANGE_SECONDS = 90 * 24 * 60 * 60;
const PAGE_SIZE = 25;

const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();

const queryValue = queryEntry =>
  Array.isArray(queryEntry) ? String(queryEntry[0] || '') : String(queryEntry || '');
const positivePage = queryEntry => {
  const parsedPage = Number.parseInt(queryValue(queryEntry), 10);
  return Number.isSafeInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1;
};

const from = ref(0);
const to = ref(0);
const page = ref(positivePage(route.query.page));
const assistantId = ref(queryValue(route.query.assistant_id));
const report = ref(null);
const isLoading = ref(false);
const errorState = ref('');
let latestRequest = 0;
let hasInitializedFilters = false;

const intlLocale = computed(() => platformIntlLocale(locale.value));
const summary = computed(() => report.value?.summary || {});
const daily = computed(() => report.value?.daily || []);
const events = computed(() => report.value?.events || []);
const assistants = computed(() => report.value?.assistants || []);
const currentBudget = computed(() => report.value?.current_account_budget || null);
const budgetSnapshot = computed(() => normalizeAiCreditSnapshot(currentBudget.value));
const assistantOptions = computed(() => [
  { value: '', label: t('AI_BOT.REPORTS.ALL_ASSISTANTS') },
  ...assistants.value.map(assistant => ({
    value: String(assistant.id),
    label: assistant.name,
  })),
]);
const pagination = computed(
  () => report.value?.pagination || { page: page.value, total: 0, total_pages: 1 }
);
const totalPages = computed(() =>
  Math.max(1, Number(pagination.value.total_pages || 1))
);

const formatNumber = count =>
  Number(count || 0).toLocaleString(intlLocale.value);
const formatUsdMicros = costMicros =>
  new Intl.NumberFormat(intlLocale.value, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  }).format(Number(costMicros || 0) / 1_000_000);
const formatUsdCents = cents => formatUsdMicros(Number(cents || 0) * 10_000);
const formatPercent = ratio => {
  if (ratio === null || ratio === undefined) return t('AI_BOT.REPORTS.NOT_AVAILABLE');

  return new Intl.NumberFormat(intlLocale.value, {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(Number(ratio));
};
const summaryMetrics = computed(() => [
  ['ASSISTED', formatNumber(summary.value.assisted_conversations)],
  ['RESOLVED', formatNumber(summary.value.resolved_conversations)],
  ['HANDED_OFF', formatNumber(summary.value.handed_off_conversations)],
  ['RESOLUTION_RATE', formatPercent(summary.value.resolution_rate)],
  ['RESPONSES', formatNumber(summary.value.response_events)],
  ['ERRORS', formatNumber(summary.value.error_events)],
  ['PROMPT_TOKENS', formatNumber(summary.value.prompt_tokens)],
  ['COMPLETION_TOKENS', formatNumber(summary.value.completion_tokens)],
  ['USD_COST', formatUsdMicros(summary.value.cost_micros)],
]);
const formatDate = dateInput => {
  const date = new Date(dateInput);
  if (!dateInput || Number.isNaN(date.getTime())) return t('AI_BOT.REPORTS.NOT_AVAILABLE');

  return new Intl.DateTimeFormat(intlLocale.value, { dateStyle: 'medium' }).format(date);
};
const formatEventDate = dateInput => {
  const date = new Date(dateInput);
  if (!dateInput || Number.isNaN(date.getTime())) return t('AI_BOT.REPORTS.NOT_AVAILABLE');

  return new Intl.DateTimeFormat(intlLocale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};
const eventLabel = eventType =>
  t(`AI_BOT.USAGE.EVENTS.${aiUsageEventLabelKey(eventType)}`);

const routeQuery = overrides => {
  const nextQuery = { ...route.query, ...overrides };
  Object.keys(nextQuery).forEach(key => {
    if (nextQuery[key] === '' || nextQuery[key] === undefined) delete nextQuery[key];
  });
  return nextQuery;
};

const fetchReport = async () => {
  if (!from.value || !to.value) return;
  if (to.value - from.value > MAX_RANGE_SECONDS) {
    report.value = null;
    errorState.value = 'range';
    return;
  }

  const requestId = ++latestRequest;
  isLoading.value = true;
  errorState.value = '';
  try {
    const response = await AiBotAPI.reports({
      from: from.value,
      to: to.value,
      assistant_id: assistantId.value || undefined,
      page: page.value,
      page_size: PAGE_SIZE,
    });
    if (requestId !== latestRequest) return;

    report.value = response.data;
    const responsePage = positivePage(report.value?.pagination?.page);
    const boundedPage = Math.min(responsePage, totalPages.value);
    if (boundedPage !== page.value) {
      page.value = boundedPage;
      await router.replace({ query: routeQuery({ page: String(boundedPage) }) });
    }
  } catch {
    if (requestId !== latestRequest) return;

    report.value = null;
    errorState.value = 'load';
  } finally {
    if (requestId === latestRequest) isLoading.value = false;
  }
};

const onFilterChange = async filters => {
  const isInitialFilterLoad = !hasInitializedFilters;
  hasInitializedFilters = true;
  from.value = filters.from;
  to.value = filters.to;
  if (!isInitialFilterLoad) page.value = 1;
  await router.replace({
    query: routeQuery({
      from: String(filters.from),
      to: String(filters.to),
      range: filters.range,
      page: String(page.value),
      group_by: undefined,
      business_hours: undefined,
    }),
  });
  fetchReport();
};

const onAssistantChange = async requestedAssistantId => {
  assistantId.value = String(requestedAssistantId || '');
  page.value = 1;
  await router.replace({
    query: routeQuery({
      assistant_id: assistantId.value || undefined,
      page: '1',
    }),
  });
  fetchReport();
};

const changePage = async pageDelta => {
  const nextPage = Math.min(totalPages.value, Math.max(1, page.value + pageDelta));
  if (nextPage === page.value) return;

  page.value = nextPage;
  await router.replace({ query: routeQuery({ page: String(nextPage) }) });
  fetchReport();
};

watch(
  () => route.query.page,
  queryPage => {
    const requestedPage = positivePage(queryPage);
    if (requestedPage === page.value) return;

    page.value = requestedPage;
    fetchReport();
  }
);

watch(
  () => route.query.assistant_id,
  queryAssistantId => {
    const requestedAssistantId = queryValue(queryAssistantId);
    if (requestedAssistantId === assistantId.value) return;

    assistantId.value = requestedAssistantId;
    page.value = 1;
    fetchReport();
  }
);
</script>

<template>
  <ReportHeader :header-title="t('AI_BOT.REPORTS.TITLE')" />
  <div class="flex flex-col gap-5">
    <div class="flex flex-col gap-3 lg:flex-row lg:items-end">
      <div class="min-w-0 flex-1">
        <ReportFilters
          :show-entity-filter="false"
          :show-group-by="false"
          :show-business-hours="false"
          :sync-route="false"
          @filter-change="onFilterChange"
        />
      </div>
      <div class="flex min-w-56 flex-col gap-1 text-xs text-n-slate-10">
        <span>{{ t('AI_BOT.REPORTS.ASSISTANT_FILTER') }}</span>
        <ComboBox
          :model-value="assistantId"
          :options="assistantOptions"
          :placeholder="t('AI_BOT.REPORTS.ALL_ASSISTANTS')"
          @update:model-value="onAssistantChange"
        />
      </div>
      <Button
        faded
        icon="i-lucide-refresh-cw"
        :label="t('AI_BOT.REPORTS.REFRESH')"
        :is-loading="isLoading"
        @click="fetchReport"
      />
    </div>

    <div
      v-if="errorState"
      role="alert"
      class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-4 py-3 text-sm text-n-ruby-11"
    >
      {{ t(`AI_BOT.REPORTS.ERRORS.${errorState.toUpperCase()}`) }}
    </div>

    <div
      v-else-if="isLoading && !report"
      role="status"
      class="rounded-lg border border-dashed border-n-weak px-4 py-10 text-center text-sm text-n-slate-10"
    >
      {{ t('AI_BOT.REPORTS.LOADING') }}
    </div>

    <template v-else-if="report">
      <section aria-labelledby="ai-bot-summary-heading">
        <h2 id="ai-bot-summary-heading" class="mb-3 text-sm font-medium text-n-slate-12">
          {{ t('AI_BOT.REPORTS.SUMMARY') }}
        </h2>
        <dl class="grid overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 sm:grid-cols-2 xl:grid-cols-3">
          <div
            v-for="metric in summaryMetrics"
            :key="metric[0]"
            class="border-b border-n-weak p-4 sm:border-r"
          >
            <dt class="text-xs text-n-slate-10">
              {{ t(`AI_BOT.REPORTS.METRICS.${metric[0]}`) }}
            </dt>
            <dd class="mt-1 text-xl font-semibold text-n-slate-12">
              {{ metric[1] }}
            </dd>
          </div>
        </dl>
      </section>

      <section v-if="currentBudget" aria-labelledby="ai-bot-budget-heading">
        <h2 id="ai-bot-budget-heading" class="mb-3 text-sm font-medium text-n-slate-12">
          {{ t('AI_BOT.CREDITS.TITLE') }}
        </h2>
        <div
          v-if="budgetSnapshot.unlimited"
          class="rounded-lg border border-n-teal-6 bg-n-teal-2 px-4 py-3 text-sm text-n-teal-11"
        >
          {{ t('AI_BOT.CREDITS.UNLIMITED_DESCRIPTION') }}
        </div>
        <dl
          v-else
          class="grid overflow-hidden rounded-lg border border-n-weak bg-n-background sm:grid-cols-2 xl:grid-cols-4"
        >
          <div class="border-b border-n-weak p-4 sm:border-r xl:border-b-0">
            <dt class="text-xs text-n-slate-10">{{ t('AI_BOT.CREDITS.BASE_LIMIT') }}</dt>
            <dd class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ formatUsdCents(budgetSnapshot.baseMonthlyLimitCents) }}
            </dd>
            <dd class="mt-1 text-xs text-n-slate-10">
              {{ t('AI_BOT.CREDITS.BASE_DETAIL', {
                remaining: formatUsdMicros(budgetSnapshot.monthlyRemainingMicros),
              }) }}
            </dd>
          </div>
          <div class="border-b border-n-weak p-4 xl:border-b-0 xl:border-r">
            <dt class="text-xs text-n-slate-10">{{ t('AI_BOT.CREDITS.WALLET_BALANCE') }}</dt>
            <dd class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ formatUsdMicros(budgetSnapshot.walletRemainingMicros) }}
            </dd>
            <dd class="mt-1 text-xs text-n-slate-10">
              {{ t('AI_BOT.CREDITS.WALLET_DETAIL', {
                credit: formatUsdCents(budgetSnapshot.walletCreditCents),
                spent: formatUsdMicros(budgetSnapshot.walletSpentMicros),
              }) }}
            </dd>
          </div>
          <div class="border-b border-n-weak p-4 sm:border-r xl:border-b-0">
            <dt class="text-xs text-n-slate-10">{{ t('AI_BOT.CREDITS.SPENT') }}</dt>
            <dd class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ formatUsdMicros(budgetSnapshot.monthlySpentMicros) }}
            </dd>
          </div>
          <div class="p-4">
            <dt class="text-xs text-n-slate-10">{{ t('AI_BOT.CREDITS.REMAINING') }}</dt>
            <dd class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ formatUsdMicros(budgetSnapshot.remainingMicros) }}
            </dd>
          </div>
        </dl>
      </section>

      <section aria-labelledby="ai-bot-daily-heading">
        <h2 id="ai-bot-daily-heading" class="mb-3 text-sm font-medium text-n-slate-12">
          {{ t('AI_BOT.REPORTS.DAILY') }}
        </h2>
        <div class="flex flex-col gap-2">
          <article
            v-for="day in daily"
            :key="day.date"
            class="grid gap-3 rounded-lg border border-n-weak bg-n-background px-4 py-3 text-sm md:grid-cols-[minmax(8rem,1fr)_repeat(4,minmax(6rem,auto))]"
          >
            <div>
              <div class="font-medium text-n-slate-12">
                {{ formatDate(`${day.date}T00:00:00Z`) }}
              </div>
              <div class="text-xs text-n-slate-10">
                {{ t('AI_BOT.REPORTS.DAILY_EVENTS', { count: formatNumber(day.events) }) }}
              </div>
            </div>
            <div>
              <div class="text-xs text-n-slate-10">{{ t('AI_BOT.REPORTS.METRICS.ASSISTED') }}</div>
              <div class="text-n-slate-12">{{ formatNumber(day.assisted_conversations) }}</div>
            </div>
            <div>
              <div class="text-xs text-n-slate-10">{{ t('AI_BOT.REPORTS.METRICS.RESOLVED') }}</div>
              <div class="text-n-slate-12">{{ formatNumber(day.resolved_conversations) }}</div>
            </div>
            <div>
              <div class="text-xs text-n-slate-10">{{ t('AI_BOT.REPORTS.METRICS.HANDED_OFF') }}</div>
              <div class="text-n-slate-12">{{ formatNumber(day.handed_off_conversations) }}</div>
            </div>
            <div>
              <div class="text-xs text-n-slate-10">{{ t('AI_BOT.REPORTS.METRICS.USD_COST') }}</div>
              <div class="text-n-slate-12">{{ formatUsdMicros(day.cost_micros) }}</div>
            </div>
          </article>
        </div>
      </section>

      <section aria-labelledby="ai-bot-events-heading">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 id="ai-bot-events-heading" class="text-sm font-medium text-n-slate-12">
            {{ t('AI_BOT.REPORTS.RECENT_EVENTS') }}
          </h2>
          <span class="text-xs text-n-slate-10">
            {{ t('AI_BOT.REPORTS.EVENT_TOTAL', { count: formatNumber(pagination.total) }) }}
          </span>
        </div>
        <div class="flex flex-col gap-2">
          <article
            v-for="event in events"
            :key="event.id"
            class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-background px-4 py-3 text-sm"
          >
            <div>
              <div class="font-medium text-n-slate-12">{{ eventLabel(event.event_type) }}</div>
              <div class="text-xs text-n-slate-10">
                {{ t('AI_BOT.REPORTS.EVENT_TOKENS', {
                  prompt: formatNumber(event.prompt_tokens),
                  completion: formatNumber(event.completion_tokens),
                }) }}
              </div>
            </div>
            <div class="text-end">
              <div class="font-medium text-n-slate-12">{{ formatUsdMicros(event.cost_micros) }}</div>
              <div class="text-xs text-n-slate-10">{{ formatEventDate(event.created_at) }}</div>
            </div>
          </article>
          <div
            v-if="!events.length"
            class="rounded-lg border border-dashed border-n-weak px-4 py-8 text-center text-sm text-n-slate-10"
          >
            {{ t('AI_BOT.REPORTS.EMPTY') }}
          </div>
        </div>
        <div
          v-if="pagination.total > PAGE_SIZE"
          class="mt-4 flex flex-wrap items-center justify-end gap-3 text-xs text-n-slate-10"
        >
          <button
            type="button"
            class="min-h-9 rounded-md border border-n-weak px-3 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="page <= 1 || isLoading"
            @click="changePage(-1)"
          >
            {{ t('AI_BOT.PAGINATION.PREVIOUS') }}
          </button>
          <span>{{ t('AI_BOT.PAGINATION.PAGE', { page, pages: totalPages }) }}</span>
          <button
            type="button"
            class="min-h-9 rounded-md border border-n-weak px-3 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="page >= totalPages || isLoading"
            @click="changePage(1)"
          >
            {{ t('AI_BOT.PAGINATION.NEXT') }}
          </button>
        </div>
      </section>
    </template>
  </div>
</template>
